import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'

export interface RejectReasonModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (reason: string) => void
  title?: string
  description?: string
  loading?: boolean
  /** Submit button label; defaults to "Reject". */
  submitLabel?: string
}

export function RejectReasonModal({
  open,
  onClose,
  onSubmit,
  title = 'Reject',
  description,
  loading = false,
  submitLabel = 'Reject',
}: RejectReasonModalProps): React.ReactElement {
  const [reason, setReason] = useState('')
  const valid = reason.trim().length >= 10

  const handleSubmit = (): void => {
    if (!valid) return
    onSubmit(reason.trim())
    setReason('')
  }

  const handleClose = (): void => {
    setReason('')
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleSubmit} disabled={!valid} loading={loading}>
            {loading ? '...' : submitLabel}
          </Button>
        </>
      }
    >
      {description && (
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">{description}</p>
      )}
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Reason
        <textarea
          className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Provide a reason (minimum 10 characters)..."
        />
      </label>
      {reason.length > 0 && reason.trim().length < 10 && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          Minimum 10 characters required ({reason.trim().length}/10)
        </p>
      )}
    </Modal>
  )
}

RejectReasonModal.displayName = 'RejectReasonModal'
